/**
 * robots.txt Generator Tool
 *
 * Generate robots.txt files with configurable rules.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { IGeneratorTool } from '@/interfaces/ITool';

export const RobotsTxtGeneratorTool: IGeneratorTool = {
  id: 'robots-txt-generator',
  name: 'robots.txt Generator',
  description: 'Generate robots.txt files with user-agent rules, paths, and sitemap',
  version: '1.0.0',
  type: 'generator',
  category: 'Generators',
  keywords: ['robots', 'txt', 'seo', 'crawler', 'sitemap', 'generator'],

  seo: {
    slug: 'robots-txt-generator',
    pageTitle: 'robots.txt Generator - Free Online Tool | SWE DevTools',
    metaDescription: 'Generate robots.txt files with user-agent rules, allow/disallow paths, sitemap URL, and crawl delay. Fast, private, runs in your browser.',
    keywords: ['robots.txt generator', 'robots txt builder', 'seo robots file', 'crawler rules generator', 'robots txt creator'],
    relatedTools: ['meta-tag-generator', 'gitignore-generator', 'slugify-generator'],
    schemaType: 'WebApplication',
  },

  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'userAgent',
      type: 'string',
      label: 'User-Agent',
      defaultValue: '*',
      description: 'Crawler user-agent (e.g., *, Googlebot, Bingbot)',
    },
    {
      name: 'allowPaths',
      type: 'string',
      label: 'Allow Paths',
      defaultValue: '/',
      description: 'Allowed paths (one per line)',
    },
    {
      name: 'disallowPaths',
      type: 'string',
      label: 'Disallow Paths',
      defaultValue: '',
      description: 'Disallowed paths (one per line, e.g., /admin, /private)',
    },
    {
      name: 'sitemapUrl',
      type: 'string',
      label: 'Sitemap URL',
      defaultValue: '',
      description: 'Full URL to sitemap.xml',
    },
    {
      name: 'crawlDelay',
      type: 'number',
      label: 'Crawl Delay (seconds)',
      defaultValue: 0,
      validation: { min: 0, max: 120, step: 1 },
      description: 'Seconds between requests (0 = none)',
    },
  ],

  async generate(config: Record<string, unknown>): Promise<string> {
    const userAgent = String(config.userAgent ?? '*').trim() || '*';
    const allowPaths = String(config.allowPaths ?? '/').trim();
    const disallowPaths = String(config.disallowPaths ?? '').trim();
    const sitemapUrl = String(config.sitemapUrl ?? '').trim();
    const crawlDelay = Number(config.crawlDelay ?? 0);

    const lines: string[] = [];

    lines.push(`User-agent: ${userAgent}`);

    // Disallow paths
    if (disallowPaths) {
      const paths = disallowPaths.split('\n').map(p => p.trim()).filter(Boolean);
      for (const path of paths) {
        lines.push(`Disallow: ${path}`);
      }
    }

    // Allow paths
    if (allowPaths) {
      const paths = allowPaths.split('\n').map(p => p.trim()).filter(Boolean);
      for (const path of paths) {
        lines.push(`Allow: ${path}`);
      }
    }

    // Crawl delay
    if (crawlDelay > 0) {
      lines.push(`Crawl-delay: ${crawlDelay}`);
    }

    // Sitemap
    if (sitemapUrl) {
      lines.push('');
      lines.push(`Sitemap: ${sitemapUrl}`);
    }

    return lines.join('\n');
  },
};
